import { ServiceLocator } from '../../app/ServiceLocator';
import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { removeDraftIdPrefix } from '../../drafts/utils/DraftUtils';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { ServerFilterClauses } from '../helpers/ServerFilterClauses';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { FlowMapper } from '../mappers/FlowMapper';
import { FlowBaseInfo, FlowBasicInfo, FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { ITaggableContent } from '../models/ITaggableContent';
import { FlowDraftInfo } from '../models/resourceInfoParts';
import { AddTagsToFlowsRequest } from '../ServerApi/requests/AddTagsToFlowsRequest';
import { CreateRunFlowTaskRequest } from '../ServerApi/requests/CreateRunFlowTaskRequest';
import { DeleteFlowDraftsRequest } from '../ServerApi/requests/DeleteFlowDraftsRequest';
import { DeleteFlowsRequest } from '../ServerApi/requests/DeleteFlowsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetFlowActionsRequest } from '../ServerApi/requests/GetFlowActionsRequest';
import { GetFlowDraftActionsRequest } from '../ServerApi/requests/GetFlowDraftActionsRequest';
import { GetFlowDraftRequest } from '../ServerApi/requests/GetFlowDraftRequest';
import { GetFlowInputStepsRequest } from '../ServerApi/requests/GetFlowInputStepsRequest';
import { GetFlowOutputStepsRequest } from '../ServerApi/requests/GetFlowOutputStepsRequest';
import { GetFlowsRequest } from '../ServerApi/requests/GetFlowsRequest';
import { MoveFlowDraftsToProjectRequest } from '../ServerApi/requests/MoveFlowDraftsToProjectRequest';
import { MoveFlowsToProjectRequest } from '../ServerApi/requests/MoveFlowsToProjectRequest';
import { PerformExtractEncryptionActionOnFlowDraftsRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnFlowDraftsRequest';
import { PerformExtractEncryptionActionOnFlowsRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnFlowsRequest';
import { RemoveTagsFromFlowsRequest } from '../ServerApi/requests/RemoveTagsFromFlowsRequest';
import { RevertFlowRequest, RevertFlowResult } from '../ServerApi/requests/RevertFlowRequest';
import { RunFlowOnOutputStepsRequest } from '../ServerApi/requests/RunFlowsOnOutputStepsRequest';
import { RunFlowsRequest } from '../ServerApi/requests/RunFlowsRequest';
import { SetFlowDescriptionRequest } from '../ServerApi/requests/SetFlowDescriptionRequest';
import { SetFlowDraftNameRequest } from '../ServerApi/requests/SetFlowDraftNameRequest';
import { SetFlowNameRequest } from '../ServerApi/requests/SetFlowNameRequest';
import { SetFlowsOwnerRequest } from '../ServerApi/requests/SetFlowsOwnerRequest';
import { FlowAllowedActions } from '../ServerApi/types/FlowAllowedActions';
import { FlowDraftAllowedActions } from '../ServerApi/types/FlowDraftAllowedActions';
import { FlowInputStep } from '../ServerApi/types/FlowInputStep';
import { FlowOutputStep } from '../ServerApi/types/FlowOutputStep';
import { UnencryptedConnectionKeychain } from '../ServerApi/types/IConnectionKeyChain';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IFlowRunSpecOutputStepViewModel } from '../ServerApi/types/IFlowRunSpecOutputStepViewModel';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class FlowsResource implements IListResource<FlowListItemInfo>, IFieldValuesResource, IDetailResource<FlowDetailInfo>, IActionResource<FlowAllowedActions>, ITaggableContent {

  private listFetcher: IListFetcher<FlowListItemInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<FlowDetailInfo>;

  constructor(
  ) {
    this.listFetcher = new WebClientListResourceFetcher(GetFlowsRequest, ServerApiMapper.flows);
    this.detailFetcher = new WebClientDetailFetcher('getFlow', FlowMapper.mapDetail);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<FlowListItemInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public actions(id: string): ng.IPromise<FlowAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowActionsRequest(id));
  }

  public addTags(ids: string[], tags: string[]): ng.IPromise<IResult> {
    const $q = ServiceLocator.get<ng.IQService>('$q');
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : $q.when({});
  }

  public removeTags(ids: string[], tags: string[]): ng.IPromise<IResult> {
    const $q = ServiceLocator.get<ng.IQService>('$q');
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : $q.when({});
  }

  public draftActions(draftId: string): ng.IPromise<FlowDraftAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowDraftActionsRequest(removeDraftIdPrefix(draftId)));
  }

  public renameDraft(draftId: string, name: string): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetFlowDraftNameRequest({ id: removeDraftIdPrefix(draftId), name: name }));
  }

  public moveDraftsToProject(targetProjectId: string, draftIds: string[]): ng.IPromise<IResult> {
    const draftIdsWithoutPrefix = draftIds.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) => new MoveFlowDraftsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): ng.IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public deleteFlowDrafts(draftIds: string[]): ng.IPromise<IResult> {
    const draftIdsWithoutPrefix = draftIds.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) => new DeleteFlowDraftsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public moveToProject(targetProjectId: string, flowIds: string[]): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(flowIds, (idsBlock) => new MoveFlowsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public getById(id: string): Promise<FlowDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getDraft(draftId: string): ng.IPromise<FlowDraftInfo> {
    return WebHttpClient.sendRequest(new GetFlowDraftRequest({ id: removeDraftIdPrefix(draftId) }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'flow');
  }

  public getFlowsByDatasourceId(datasourceId: string): ng.IPromise<FlowListItemInfo[]> {
    const query = this.all().addFilter(ServerFilterClauses.serverFilterClauses.datasource(datasourceId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public getInputSteps(flowId: string): ng.IPromise<ReadonlyArray<FlowInputStep>> {
    return WebHttpClient.sendRequest(new GetFlowInputStepsRequest({ flowId }))
      .then((result) => result.inputSteps || []); // what to do in the case of an error?
  }

  public getOutputSteps(flowId: string): ng.IPromise<ReadonlyArray<FlowOutputStep>> {
    return WebHttpClient.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => result.outputSteps || []); // what to do in the case of an error?
  }

  public getOutputStepInfos(flowId: string): Promise<ReadonlyArray<FlowOutputStepInfo>> {
    return WebHttpClient.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => ServerApiMapper.flowOutputSteps(result));
  }

  public rename(id: string, name: string): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetFlowNameRequest({ id: id, name: name }));
  }

  public runFlow(ids: string[], isRunAllIncremental: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunFlowsRequest(idsBlock, isRunAllIncremental)),
      ResultHelper.combineResults
    );
  }

  public runFlowOnOutputSteps(flowId: string, flowOutputStepIds: string[], isIncremental: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new RunFlowOnOutputStepsRequest({
        flowId: flowId,
        flowOutputStepIds: flowOutputStepIds,
        flowRunSpecOutputStepParams: flowOutputStepIds.map(id => ({ isIncremental: isIncremental, flowOutputStepId: parseInt(id, 10) }))
      }));
  }

  public setDescription(id: string, description: string): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetFlowDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetFlowsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public createRunFlowTask(id: string, scheduleId: string, runAllOutputs: boolean, isRunAllIncremental: boolean,
    flowOutputStepIds: string[], flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[]): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateRunFlowTaskRequest({
        scheduleId: scheduleId,
        flowId: id,
        runAllOutputs: runAllOutputs,
        isRunAllIncremental: isRunAllIncremental,
        flowOutputStepIds: flowOutputStepIds,
        flowRunSpecOutputStepParams: flowRunSpecOutputStepParams
      }));
  }

  public revertFlow(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): ng.IPromise<RevertFlowResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RevertFlowRequest({
      id,
      version,
      keychains,
      encryptedPasswordKeyId
    }));
  }

  public deleteFlows(ids: string[]): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteFlowsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public encryptFlows(ids: string[], actionType: string): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  // Encrypt unparented flow draft
  public encryptFlowDrafts(ids: string[], actionType: string): ng.IPromise<IResult> {
    const draftIdsWithoutPrefix = ids.map(id => {
      return removeDraftIdPrefix(id);
    });
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(draftIdsWithoutPrefix, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowDraftsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults
    );
  }

  public hasDataQualityWarning(contentItem: FlowBaseInfo): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public isFlowInfo(item: any): item is FlowBasicInfo {
    return item &&
      item.contentType &&
      item.contentType === 'flow' &&
      item.hasOwnProperty('project');
  }
}

export const Flows = new FlowsResource();
